import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { MapPin, Plus, X, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MonthYearPicker } from "@/components/MonthYearPicker";
import LocalDeals from "@/components/LocalDeals";
import { CoverUpload, GalleryUpload, DocUpload } from "@/components/PropertyUploads";

export const PROPERTY_TYPES = ["Residential Plots", "Flats / Apartments", "Commercial Bays", "Shops / SCO", "Commercial Property", "Residential Kothi / House", "Villas", "Industrial Property", "Other"];
const STATES = ["Punjab", "Himachal Pradesh", "Chandigarh", "Haryana"];
const APPROVAL_STATUSES = ["Registered", "Approved", "Applied", "Pending"];
const fieldCls = "mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm";

function ApprovalFields({ kind, data, set, onBusy }) {
  const k = kind.toLowerCase();
  return (
    <>
      <div className="md:col-span-2 pt-2 border-t border-[#1E293B] text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{kind} Details</div>
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400">{kind} Status</label>
        <select value={data[`${k}_status`] || ""} onChange={e => set(`${k}_status`, e.target.value)} data-testid={`prop-${k}-status`} className={fieldCls}>
          <option value="">— Select —</option>
          {APPROVAL_STATUSES.map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
      {[[`${kind} Registration / Approval No.`, `${k}_number`], ...(kind === "RERA" ? [] : [[`${kind} Authority`, `${k}_authority`]])].map(([label, key]) => (
        <div key={key}>
          <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
          <input type="text" value={data[key] || ""} onChange={e => set(key, e.target.value)} data-testid={`prop-field-${key}`} className={fieldCls} />
        </div>
      ))}
      <div className={kind === "RERA" ? "md:col-span-2" : ""}>
        <label className="text-xs uppercase tracking-widest text-slate-400">{kind} Date (MM/YYYY)</label>
        <MonthYearPicker value={data[`${k}_date`]} onChange={v => set(`${k}_date`, v)} testid={`prop-field-${k}_date`} className={fieldCls} />
      </div>
      <div className="md:col-span-2">
        <DocUpload label={`${kind} Certificate`} value={data[`${k}_certificate`]} onChange={v => set(`${k}_certificate`, v)} testid={`${k}-cert-upload-input`} onBusy={onBusy} />
      </div>
    </>
  );
}

function PropertyForm({ initial, onSave, onClose, onDelete }) {
  const { user } = useAuth();
  const [data, setData] = useState(initial || { project_name: "", sector: "", location: "Mohali", plot_size: "", price: "", availability: "Available", description: "", image_url: "", brochure_url: "", map_url: "" });
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const [uploading, setUploading] = useState(0);
  const onBusy = (n) => setUploading(u => u + n);
  const submit = async (e) => {
    e.preventDefault();
    try { await onSave(data); toast.success("Property saved"); onClose(); }
    catch { toast.error("Failed"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete property "${initial.project_name}"? This cannot be undone.`)) return;
    try { await onDelete(initial.id); toast.success("Property deleted"); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-[#0A1120] border border-[#1E293B] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="property-form-modal">
        <div className="p-5 border-b border-[#1E293B] flex items-center justify-between">
          <h3 className="font-serif text-xl text-white">{initial ? "Edit Property" : "New Property"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">State / UT*</label>
            <select required value={data.state || ""} onChange={e => set("state", e.target.value)} data-testid="prop-state" className={fieldCls}>
              <option value="">— Select —</option>
              {STATES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Property Type</label>
            <select value={data.property_type || ""} onChange={e => set("property_type", e.target.value)} data-testid="prop-property-type" className={fieldCls}>
              <option value="">— Select —</option>
              {PROPERTY_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          {[["Project Name*", "project_name", true], ["Sector", "sector"], ["Location", "location"], ["Plot Sizes / Types", "plot_size"], ["Selling Price", "price"], ["On-Board Price", "onboard_price"], ["Owner / Developer Name", "owner_name"], ["Total Land Area", "land_area"], ["Total Plots / Units", "total_units"], ["Expected Possession Date", "possession_date", false, "date"], ["Image URL (optional)", "image_url"], ["Brochure URL (optional)", "brochure_url"], ["Google Maps URL", "map_url"], ["Salesperson Name", "salesperson_name"], ["Salesperson Contact Number", "salesperson_phone", false, "tel"]].map(([label, key, req, type]) => (
            <div key={key}>
              <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
              <input required={req} type={type || "text"} value={data[key] || ""} onChange={e => set(key, e.target.value)}
                data-testid={`prop-field-${key}`}
                className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Availability</label>
            <select value={data.availability} onChange={e => set("availability", e.target.value)} data-testid="prop-availability"
              className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm">
              {["Available", "Few Left", "Sold Out"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-400">Project Description</label>
            <textarea rows={3} value={data.description || ""} onChange={e => set("description", e.target.value)} data-testid="prop-field-description"
              className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-400">Amenities & Facilities</label>
            <textarea rows={3} value={data.amenities || ""} onChange={e => set("amenities", e.target.value)} data-testid="prop-field-amenities" className={fieldCls} />
          </div>
          <div className="md:col-span-2 pt-2 border-t border-[#1E293B] text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Photos & Documents</div>
          <div className="md:col-span-2"><CoverUpload value={data.cover_photo} onChange={v => set("cover_photo", v)} onBusy={onBusy} /></div>
          <div className="md:col-span-2"><GalleryUpload value={data.gallery} onChange={v => set("gallery", v)} onBusy={onBusy} /></div>
          <div className="md:col-span-2"><DocUpload label="Brochure (PDF / Image)" accept="application/pdf,image/*" value={data.brochure_file} onChange={v => set("brochure_file", v)} testid="brochure-upload-input" onBusy={onBusy} /></div>
          <ApprovalFields kind="RERA" data={data} set={set} onBusy={onBusy} />
          <ApprovalFields kind="CLU" data={data} set={set} onBusy={onBusy} />
        </div>
        <div className="p-5 border-t border-[#1E293B] flex justify-end gap-2">
          {initial && user?.role === "admin" && (
            <button type="button" onClick={remove} data-testid="prop-delete-btn" className="mr-auto px-4 py-2 rounded-md text-sm text-rose-400 border border-rose-400/30 hover:bg-rose-400/10">Delete Property</button>
          )}
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-300">Cancel</button>
          <button data-testid="prop-save-btn" disabled={uploading > 0} className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E] disabled:opacity-50">{uploading > 0 ? "Uploading…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

const AVAIL_STYLES = {
  Available: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
  "Few Left": "text-amber-300 bg-amber-400/10 border-amber-400/20",
  "Sold Out": "text-rose-300 bg-rose-400/10 border-rose-400/20",
};

export default function Properties() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("Punjab");
  const unassigned = items.filter(p => !STATES.includes(p.state));
  const visible = tab === "Unassigned" ? unassigned : items.filter(p => p.state === tab);
  const tabs = [...STATES, "Local Deals", ...(unassigned.length ? ["Unassigned"] : [])];
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const reqId = useRef(0);

  const load = async () => {
    const id = ++reqId.current;
    setStatus(s => (s === "ready" ? s : "loading"));
    try {
      const r = await api.get("/properties", { params: search ? { search } : {}, timeout: 20000 });
      if (id !== reqId.current) return;
      setItems(Array.isArray(r.data) ? r.data : []);
      setStatus("ready");
    } catch (err) {
      if (id !== reqId.current) return;
      const d = err?.response?.data?.detail;
      setErrorMsg(typeof d === "string" ? d : err.code === "ECONNABORTED" ? "Request timed out" : "Could not load properties");
      setStatus("error");
    }
  };
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (d) => {
    if (editing) await api.patch(`/properties/${editing.id}`, d);
    else await api.post("/properties", d);
    await load();
  };

  const removeProperty = async (id) => {
    await api.delete(`/properties/${id}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Inventory</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">Signature Properties</h1>
        </div>
        <div className="flex gap-2">
          <input placeholder={tab === "Local Deals" ? "Search owner, phone, location…" : "Search project, sector…"} value={search} onChange={e => setSearch(e.target.value)} data-testid="prop-search"
            className="bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-4 py-2 text-sm text-white" />
          {tab !== "Local Deals" && <button onClick={() => { setEditing(null); setShow(true); }} data-testid="new-property-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">
            <Plus weight="bold" /> Add
          </button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="state-tabs">
        {tabs.map(t => {
          const n = t === "Unassigned" ? unassigned.length : items.filter(p => p.state === t).length;
          return (
            <button key={t} onClick={() => setTab(t)} data-testid={`state-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${tab === t ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#1E293B] text-slate-300 hover:border-[#D4AF37]/40"}`}>
              {t} {t !== "Local Deals" && <span className="ml-1 opacity-70">({n})</span>}
            </button>
          );
        })}
      </div>

      {tab === "Local Deals" ? <LocalDeals search={search} /> : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {status === "loading" && <div className="col-span-full text-slate-400 text-sm text-center py-10" data-testid="properties-loading">Loading properties…</div>}
        {status === "error" && (
          <div className="col-span-full text-center py-10 space-y-3" data-testid="properties-error">
            <div className="text-rose-300 text-sm">Couldn't load properties: {errorMsg}</div>
            <button onClick={() => { setStatus("loading"); load(); }} data-testid="properties-retry-btn" className="px-4 py-2 rounded-md border border-[#D4AF37]/40 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10">Retry</button>
          </div>
        )}
        {status === "ready" && items.length === 0 && <div className="col-span-full text-slate-500 text-sm text-center py-10" data-testid="properties-empty">No properties yet.</div>}
        {status === "ready" && items.length > 0 && visible.length === 0 && <div className="col-span-full text-slate-500 text-sm text-center py-10" data-testid="properties-tab-empty">No properties in {tab} yet.</div>}
        {status !== "error" && visible.map(p => (
          <div key={p.id} className="card-luxury overflow-hidden group" data-testid="property-card">
            <Link to={`/properties/${p.id}`} data-testid={`property-photo-${p.id}`} className="relative block aspect-[4/3] overflow-hidden bg-[#060B14]">
              {p.cover_photo?.path || p.image_url ? (
                <img src={p.cover_photo?.path ? fileUrl(p.cover_photo.path) : p.image_url} alt={p.project_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-600">No image</div>
              )}
              <div className="absolute top-3 right-3">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${AVAIL_STYLES[p.availability] || ""}`}>{p.availability}</span>
              </div>
              {p.property_type && (
                <div className="absolute top-3 left-3">
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#D4AF37]/30 bg-[#060B14]/80 text-[#D4AF37]" data-testid="property-type-label">{p.property_type}</span>
                </div>
              )}
            </Link>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-lg text-white">{p.project_name}</h3>
              </div>
              <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPin size={12} weight="duotone" /> {p.sector} • {p.location}</div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Size</div>
                  <div className="text-sm text-white">{p.plot_size}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Price</div>
                  <div className="text-lg text-[#D4AF37] font-serif">₹{p.price}</div>
                </div>
              </div>
              {p.description && <p className="text-xs text-slate-400 mt-3 line-clamp-2">{p.description}</p>}
              <div className="mt-4 flex gap-2 pt-4 border-t border-[#1E293B]">
                {[["View Map", p.map_url, "map"], ["Brochure", p.brochure_file?.path ? fileUrl(p.brochure_file.path) : p.brochure_url, "brochure"]].map(([label, url, k]) => (
                  <a key={k} href={url || "#"} target="_blank" rel="noreferrer" data-testid={`property-${k}-btn`}
                    onClick={e => { if (!url) { e.preventDefault(); toast.info("Not available"); } }}
                    className="text-xs text-[#D4AF37] hover:underline">{label}</a>
                ))}
                <a href={`https://wa.me/?text=${encodeURIComponent(`Check out ${p.project_name} at ${p.location} — ₹${p.price}. ${p.map_url || ""}${p.brochure_file?.path || p.brochure_url ? `\nBrochure: ${p.brochure_file?.path ? `${process.env.REACT_APP_BACKEND_URL}/api/public/brochure/${p.id}` : p.brochure_url}` : ""}`)}`} target="_blank" rel="noreferrer" data-testid="property-share-btn" className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1 ml-auto">
                  <WhatsappLogo size={12} /> Share
                </a>
                <button onClick={() => { setEditing(p); setShow(true); }} className="text-xs text-slate-400 hover:text-white">Edit</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {show && <PropertyForm initial={editing} onSave={save} onDelete={removeProperty} onClose={() => setShow(false)} />}
    </div>
  );
}
